import { useEffect, useRef } from 'react';
import { useRealtime } from '../contexts/RealtimeContext';
import type { RealtimeEventName, RealtimeEventPayload, ServerToClientEvents } from '../types/realtime';

type EventHandler<TEvent extends RealtimeEventName> = (payload: RealtimeEventPayload<TEvent>) => void;

export const useRealtimeEvent = <TEvent extends RealtimeEventName>(
  event: TEvent,
  handler: EventHandler<TEvent>
) => {
  const { socket } = useRealtime();
  const savedHandler = useRef<EventHandler<TEvent>>(handler);

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!socket) {
      return undefined;
    }

    const listener: ServerToClientEvents[TEvent] = ((payload: RealtimeEventPayload<TEvent>) => {
      savedHandler.current(payload);
    }) as ServerToClientEvents[TEvent];

    const realtimeSocket = socket as unknown as {
      on: <U extends RealtimeEventName>(eventName: U, eventListener: ServerToClientEvents[U]) => void;
      off: <U extends RealtimeEventName>(eventName: U, eventListener: ServerToClientEvents[U]) => void;
    };

    realtimeSocket.on(event, listener);

    return () => {
      realtimeSocket.off(event, listener);
    };
  }, [event, socket]);
};
